import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEURCompact, daysUntil } from "@/lib/api";
import { UsersThree, Heart, ArrowRight, Warning, ChatCircleText, Check, Phone, ChatText, WhatsappLogo, Moon } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIER_BG = { A: "var(--cf-success-soft)", B: "var(--cf-warning-soft)", C: "var(--cf-danger-soft)" };
const TIER_TXT = { A: "var(--cf-success-text)", B: "var(--cf-warning-text)", C: "var(--cf-danger-text)" };

/**
 * Bloc "Suivi apporteur" — feedback dossier après relance.
 *
 * Affiche les tâches créées automatiquement par le hook POST /dossiers/{id}/relance :
 * quand on relance un dossier avec apporteur, une tâche "Informer X — Sujet : suivi du dossier Y"
 * apparaît ici jusqu'à ce qu'on clique "Marquer informé".
 *
 * Source : GET /apporteur-followups (anti-doublon backend par couple dossier×apporteur×slot).
 * Safe = Silence : aucun item → bloc invisible.
 */
export function SuiviApporteurDossier({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const res = await api.get("/apporteur-followups");
      setItems(res.data?.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [refreshKey]);

  const markInformed = async (id) => {
    setMarking(id);
    try {
      await api.post(`/apporteur-followups/${id}/mark-informed`);
      toast.success("Apporteur informé");
      // Optimistic : retire de la liste
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setMarking(null);
    }
  };

  if (loading) {
    return <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 animate-pulse h-24" data-testid="suivi-apporteur-dossier-loading" />;
  }
  if (items.length === 0) return null; // Safe = Silence

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
      data-testid="commando-suivi-apporteur-dossier"
    >
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
        <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-slate-900 flex items-center gap-1.5">
          <ChatCircleText size={13} weight="fill" color="var(--cf-primary)" /> Suivi apporteur
        </h3>
        <span className="text-[10px] text-slate-500 font-semibold">{items.length} à informer</span>
      </div>
      <div className="p-2.5 space-y-1.5">
        {items.map((f) => (
          <div
            key={f.id}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex items-center gap-2"
            data-testid={`suivi-apporteur-dossier-${f.id}`}
          >
            <button
              onClick={() => navigate(`/dossier/${f.dossier_id}`)}
              className="flex-1 min-w-0 text-left hover:bg-slate-50 rounded -mx-1 px-1 py-0.5"
              data-testid={`suivi-apporteur-dossier-${f.id}-open`}
            >
              <div className="font-bold text-[13.5px] text-slate-900 truncate">
                Informer {f.apporteur_name}
              </div>
              <div className="text-[10.5px] text-slate-500 truncate">
                Sujet : suivi du dossier <span className="font-semibold text-slate-700">{f.dossier_nom}</span>
              </div>
            </button>
            <button
              onClick={() => markInformed(f.id)}
              disabled={marking === f.id}
              className="px-2.5 py-1.5 rounded-lg bg-[color:var(--cf-primary)] text-white font-bold text-[11.5px] flex items-center gap-1 hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-50 flex-shrink-0"
              data-testid={`suivi-apporteur-dossier-${f.id}-mark`}
            >
              <Check size={11} weight="bold" />
              {marking === f.id ? "..." : "Marquer informé"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Bloc "Apporteurs à recontacter" pour Commando.
 * Affiche uniquement les apporteurs DUS aujourd'hui (next_relance <= today, non en retard critique).
 * Le retard critique vit déjà sur /relances/retard.
 * Max 3 entrées visibles + compteur muet.
 */
export function SuiviApporteur({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/apporteurs/stats?sort=retard");
        if (!cancelled) {
          const due = (res.data?.list || []).filter((a) => {
            const dn = a.days_until_next;
            // Inclure : dus aujourd'hui (0) ou demain (1) ou retard léger (jusqu'à -3)
            return dn !== null && dn <= 1 && dn >= -3;
          });
          setItems(due);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 animate-pulse h-24" data-testid="suivi-apporteur-loading" />;
  }
  if (items.length === 0) return null; // doctrine : safe = silence

  const visible = items.slice(0, 3);
  const hidden = items.length - visible.length;

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
      data-testid="commando-suivi-apporteur"
    >
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
        <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-slate-900 flex items-center gap-1.5">
          <UsersThree size={13} weight="fill" color="var(--cf-primary)" /> Apporteurs à recontacter
        </h3>
        <span className="text-[10px] text-slate-500 font-semibold">selon Tier</span>
      </div>
      <div className="p-2.5 space-y-1.5">
        {visible.map((a) => {
          const dn = a.days_until_next;
          const late = dn < 0;
          return (
            <button
              key={a.name}
              onClick={() => navigate("/apporteurs")}
              className="w-full text-left rounded-xl border border-slate-200 bg-white hover:border-slate-300 px-3 py-2.5 flex items-center gap-2"
              data-testid={`suivi-apporteur-${a.name}`}
            >
              <span
                className="text-[10px] font-extrabold rounded-full w-5 h-5 flex items-center justify-center"
                style={{ background: TIER_BG[a.tier] || "var(--cf-bg-2)", color: TIER_TXT[a.tier] || "var(--cf-neutral-text)" }}
              >
                {a.tier}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[13.5px] text-slate-900 truncate">{a.name}</div>
                <div className="text-[10.5px] text-slate-500">
                  {late ? <span className="text-rose-600 font-bold">en retard {-dn}j</span> : dn === 0 ? "à recontacter aujourd'hui" : `dans ${dn}j`}
                </div>
              </div>
              <ArrowRight size={12} weight="bold" className="text-slate-400" />
            </button>
          );
        })}
        {hidden > 0 && (
          <div className="text-center py-1.5 text-[10.5px] font-semibold text-slate-400">
            +{hidden} autre{hidden > 1 ? "s" : ""} apporteur{hidden > 1 ? "s" : ""}
          </div>
        )}
      </div>
    </section>
  );
}

const CLIENT_STEP_LABEL = {
  satisfaction: "Enquête satisfaction",
  recommandation: "Demande recommandation",
  patrimoniale: "Reprise patrimoniale",
};

/**
 * Bloc "Suivi client" pour Commando.
 * Réunit 2 signaux :
 *   - cycles post-signature (satisfaction J+7 / recommandation M+4 / patrimoniale M+10)
 *   - anniversaires emprunteur(s) aujourd'hui (jour+mois indépendants de l'année)
 *
 * Safe = Silence : aucun item → bloc invisible.
 */
export function SuiviClient({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // On a besoin de TOUS les dossiers actifs : les signés pour le cycle
        // post-signature ET les non-signés pour détecter les anniversaires.
        const res = await api.get("/dossiers");
        if (cancelled) return;
        const today = new Date();
        const tmm = today.getMonth() + 1;
        const tdd = today.getDate();

        // Robust DOB parser : accepte "YYYY-MM-DD", "YYYY-MM-DDTHH:MM:SS",
        // "DD/MM/YYYY" et "DD-MM-YYYY". Renvoie {month, day} ou null.
        const parseDob = (raw) => {
          if (!raw || typeof raw !== "string") return null;
          const s = raw.trim();
          // ISO (YYYY-MM-DD[Tetc])
          let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return { month: parseInt(m[2], 10), day: parseInt(m[3], 10) };
          // FR DD/MM/YYYY ou DD-MM-YYYY
          m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
          if (m) return { month: parseInt(m[2], 10), day: parseInt(m[1], 10) };
          return null;
        };

        const out = [];
        for (const d of res.data || []) {
          // Anniversaire = signal client. On exclut UNIQUEMENT les dossiers perdus.
          // Les dossiers en stand-by gardent leur anniversaire (le client existe toujours).
          if (d.archived || d.status === "perdu") continue;

          // Cycle post-signature (seulement sur dossiers signés)
          if (d.status === "signe" && !d.is_standby) {
            const rd = d.relance_client_date;
            if (rd) {
              const dn = daysUntil(rd);
              if (dn !== null && dn <= 1 && dn >= -3) {
                out.push({
                  key: `cycle:${d.id}`,
                  kind: "cycle",
                  dossier: d,
                  step: d.relance_client_step || "satisfaction",
                  dn,
                  sortKey: dn,
                });
              }
            }
          }

          // Anniversaires emprunteur(s) — aujourd'hui uniquement (jour+mois)
          for (const idx of [1, 2]) {
            const dob = d[`client_${idx}_dob`];
            const parsed = parseDob(dob);
            if (!parsed) continue;
            if (parsed.month === tmm && parsed.day === tdd) {
              const prenom = d[`client_${idx}_prenom`] || "";
              const nom = d[`client_${idx}_nom`] || "";
              const fullName = `${prenom} ${nom}`.trim() || d.client_name || d.nom || "Emprunteur";
              out.push({
                key: `birthday:${d.id}:${idx}`,
                kind: "birthday_due",
                dossier: d,
                emprunteur: fullName,
                phone: d.client_phone || null,
                sortKey: -100, // anniversaires en tête (priorité émotionnelle)
              });
            }
          }
        }
        out.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
        setItems(out);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 animate-pulse h-24" data-testid="suivi-client-loading" />;
  }
  if (items.length === 0) return null;

  const visible = items.slice(0, 5);
  const hidden = items.length - visible.length;

  const buildWaUrl = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^\d+]/g, "");
    return `https://wa.me/${cleaned.replace(/^\+/, "")}`;
  };

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
      data-testid="commando-suivi-client"
    >
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
        <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-slate-900 flex items-center gap-1.5">
          <Heart size={13} weight="fill" color="var(--cf-accent)" /> Suivi client
        </h3>
        <span className="text-[10px] text-slate-500 font-semibold">{visible.length} action{visible.length > 1 ? "s" : ""}</span>
      </div>
      <div className="p-2.5 space-y-1.5">
        {visible.map((it) => {
          const d = it.dossier;
          if (it.kind === "birthday_due") {
            const waUrl = buildWaUrl(it.phone);
            return (
              <div
                key={it.key}
                className="rounded-xl border border-pink-200 bg-pink-50/40 px-3 py-2.5"
                data-testid={`suivi-client-birthday-${d.id}`}
              >
                <button
                  onClick={() => navigate(`/dossier/${d.id}`)}
                  className="block w-full text-left -mx-1 px-1 mb-1.5"
                  data-testid={`suivi-client-birthday-${d.id}-open`}
                >
                  <div className="font-bold text-[13.5px] text-slate-900 truncate">
                    Souhaiter anniversaire — {it.emprunteur}
                  </div>
                  <div className="text-[10.5px] text-slate-500 truncate">
                    <span className="font-bold text-pink-600">Anniversaire aujourd'hui</span>
                    <span className="mx-1">·</span>
                    {d.nom}
                  </div>
                </button>
                <div className="flex items-center gap-1.5">
                  {it.phone ? (
                    <>
                      <a
                        href={`tel:${it.phone}`}
                        className="flex-1 text-center px-2 py-1.5 rounded-lg bg-white border border-slate-200 text-[11.5px] font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1"
                        data-testid={`birthday-${d.id}-call`}
                      >
                        <Phone size={11} weight="bold" /> Appeler
                      </a>
                      <a
                        href={`sms:${it.phone}`}
                        className="flex-1 text-center px-2 py-1.5 rounded-lg bg-white border border-slate-200 text-[11.5px] font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1"
                        data-testid={`birthday-${d.id}-sms`}
                      >
                        <ChatText size={11} weight="bold" /> SMS
                      </a>
                      <a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center px-2 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[11.5px] font-bold text-emerald-700 hover:bg-emerald-100 inline-flex items-center justify-center gap-1"
                        data-testid={`birthday-${d.id}-wa`}
                      >
                        <WhatsappLogo size={11} weight="bold" /> WhatsApp
                      </a>
                    </>
                  ) : (
                    <button
                      onClick={() => navigate(`/dossier/${d.id}`)}
                      className="flex-1 text-center px-2 py-1.5 rounded-lg bg-white border border-dashed border-slate-300 text-[11px] font-semibold text-slate-500 hover:bg-slate-50"
                      data-testid={`birthday-${d.id}-fallback`}
                    >
                      Ouvrir la fiche dossier pour récupérer le contact
                    </button>
                  )}
                </div>
              </div>
            );
          }
          // Cycle post-signature
          const dn = it.dn;
          const late = dn !== null && dn < 0;
          return (
            <button
              key={it.key}
              onClick={() => navigate(`/dossier/${d.id}`)}
              className="w-full text-left rounded-xl border border-slate-200 bg-white hover:border-slate-300 px-3 py-2.5 flex items-center gap-2"
              data-testid={`suivi-client-${d.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[13.5px] text-slate-900 truncate">{d.nom}</div>
                <div className="text-[10.5px] text-slate-500 truncate">
                  <span className="font-bold text-pink-600">{CLIENT_STEP_LABEL[it.step] || it.step}</span>
                  <span className="mx-1">·</span>
                  {late ? <span className="text-rose-600 font-bold">en retard {-dn}j</span> : dn === 0 ? "aujourd'hui" : `dans ${dn}j`}
                </div>
              </div>
              <ArrowRight size={12} weight="bold" className="text-slate-400" />
            </button>
          );
        })}
        {hidden > 0 && (
          <div className="text-center py-1.5 text-[10.5px] font-semibold text-slate-400">
            +{hidden} autre{hidden > 1 ? "s" : ""} action{hidden > 1 ? "s" : ""}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Bouton "Relances en retard" — visible UNIQUEMENT s'il existe des retards.
 * Click → /relances/retard
 */
export function RetardCTA({ refreshKey }) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [doss, app] = await Promise.all([
          api.get("/dossiers"),
          api.get("/apporteurs/stats"),
        ]);
        if (cancelled) return;
        const dossLate = (doss.data || []).filter((d) => {
          if (d.archived || d.is_standby || d.status === "perdu") return false;
          if (!d.next_relance_date) return false;
          const dn = daysUntil(d.next_relance_date);
          return dn !== null && dn < 0;
        }).length;
        const appLate = (app.data?.list || []).filter((a) => a.late).length;
        setCount(dossLate + appLate);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading || count === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate("/relances/retard")}
      className="w-full mb-4 rounded-xl bg-rose-50 border border-rose-200 hover:border-rose-300 px-4 py-3 flex items-center justify-between gap-3 transition-colors"
      data-testid="commando-retard-cta"
    >
      <span className="flex items-center gap-2.5">
        <Warning size={16} weight="fill" color="var(--cf-danger-text)" />
        <span className="text-[14px] font-extrabold text-rose-700">
          {count} relance{count > 1 ? "s" : ""} en retard
        </span>
      </span>
      <ArrowRight size={14} weight="bold" className="text-rose-500" />
    </button>
  );
}

/**
 * Bloc "Dossiers à reprendre" — dossiers en stand-by dont la date de reprise est atteinte/passée.
 * Source : GET /standby/to-resume (filtre serveur).
 * Safe = Silence : aucun item → bloc invisible.
 * Le user décide explicitement : ouvrir, réactiver, prolonger, archiver, marquer perdu.
 */
export function DossiersAReprendre({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const res = await api.get("/standby/to-resume");
      setItems(res.data?.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchData(); }, [refreshKey]);

  const reactivate = async (id) => {
    setReactivating(id);
    try {
      await api.post(`/dossiers/${id}/reactivate`);
      toast.success("Dossier réactivé");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setReactivating(null);
    }
  };

  if (loading) {
    return <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 animate-pulse h-24" data-testid="dossiers-a-reprendre-loading" />;
  }
  if (items.length === 0) return null;

  return (
    <section
      className="rounded-xl border border-violet-200 bg-violet-50/30 mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)] da-standby"
      data-testid="commando-dossiers-a-reprendre"
    >
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-violet-100">
        <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-violet-900 flex items-center gap-1.5">
          <Moon size={13} weight="fill" color="var(--cf-accent)" /> Dossiers à reprendre
        </h3>
        <span className="text-[10px] text-violet-600 font-semibold">{items.length} décision{items.length > 1 ? "s" : ""}</span>
      </div>
      <div className="p-2.5 space-y-1.5">
        {items.map((d) => {
          const daysOverdue = (() => {
            try {
              const u = new Date(d.standby_until + "T00:00:00");
              const t = new Date();
              t.setHours(0, 0, 0, 0);
              return Math.round((t - u) / 86_400_000);
            } catch { return null; }
          })();
          return (
            <div
              key={d.id}
              className="rounded-xl border border-violet-200 bg-white px-3 py-2.5 flex items-center gap-2"
              data-testid={`a-reprendre-${d.id}`}
            >
              <button
                onClick={() => navigate(`/dossier/${d.id}`)}
                className="flex-1 min-w-0 text-left hover:bg-violet-50 rounded -mx-1 px-1 py-0.5"
                data-testid={`a-reprendre-${d.id}-open`}
              >
                <div className="font-bold text-[13.5px] text-slate-900 truncate">
                  Reprendre dossier — {d.nom}
                </div>
                <div className="text-[10.5px] text-slate-500 truncate">
                  {d.standby_reason && <span>{d.standby_reason}</span>}
                  {d.standby_reason && <span className="mx-1">·</span>}
                  <span className="font-bold text-violet-700">
                    {daysOverdue === 0 ? "À reprendre aujourd'hui" : daysOverdue > 0 ? `Reprise prévue il y a ${daysOverdue}j` : ""}
                  </span>
                </div>
              </button>
              <button
                onClick={() => reactivate(d.id)}
                disabled={reactivating === d.id}
                className="px-2.5 py-1.5 rounded-lg bg-violet-600 text-white font-bold text-[11.5px] hover:bg-violet-700 disabled:opacity-50 flex-shrink-0"
                data-testid={`a-reprendre-${d.id}-reactivate`}
              >
                {reactivating === d.id ? "..." : "Réactiver"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
